import { RouteFactory } from "@app/routePaths";
import pulseOgImage from "@assets/pulse-og-image.jpg";
import { GADataProps } from "@components/GAData/GAData";
import { URL_ORIGIN } from "@components/MetaTags/constants";
import {
  getUpdatedOgImageUrl,
  renderPageTitleV2,
} from "@components/MetaTags/helpers";
import { TrackingObject } from "@features/analytics/utils/types";
import { dayjsSingaporeTimezone, getEnvironment } from "@util/constant";
import { getEnvironmentDomain, getFormattedUrlPath } from "@util/helpers";
import dayjs from "dayjs";

import type { FeedPulseItems, PulseShareCount } from "./types";
import { SanitizedPulseData } from "./types";

export const getActiveArticleId = (pathname: string): string => {
  const activeArticleId = pathname.split("/");
  const lastItem = activeArticleId[activeArticleId.length - 1];

  return /^[0-9]+$/.test(lastItem) ? lastItem : "";
};

export const getGaDataObject = (
  title: string = "pulse intro",
  virtualUrl: string = "pulse/intro",
  id: string = "",
  articles?: SanitizedPulseData[],
  pagination?: string
): TrackingObject => {
  const gaObject: TrackingObject = {
    event: "virtual_pv",
    level2: "pulse",
    contentcat: "free",
    contenttype: "listing",
    title,
    virtual_url: virtualUrl,
    pagination: pagination || "1",
  };

  if (id !== "" && articles) {
    const currentSlideData = articles.find((article) => article.id === id);
    gaObject["articleid"] = id;
    gaObject["cue_articleid"] = id;
    gaObject["author"] = currentSlideData?.authors || "(not set)";
    gaObject["pubdate"] =
      dayjs(currentSlideData?.published)
        .tz(dayjsSingaporeTimezone)
        .format("YYYY-MM-DD HH:mm:ss") || "(not set)";
    gaObject["title"] = currentSlideData?.title || "(not set)";
    gaObject["contenttype"] = "article";
    gaObject["virtual_url"] = currentSlideData?.slug || "(not set)";
  }

  return gaObject;
};

export const sanitizedPulseData = (data: FeedPulseItems[]) => {
  const santitizedData: Promise<SanitizedPulseData>[] = data
    .filter((x) => x.id)
    .map(async (article): Promise<SanitizedPulseData> => {
      const environmentDomain = getEnvironmentDomain(getEnvironment());
      const slug = RouteFactory.pulseSlide(article.id);
      const longURL = `${environmentDomain}${slug}`;
      const shortUrl = article.shortener;

      const imageParmeters = [
        { key: "w", value: "900" },
        { key: "f", value: "webp" },
      ];

      const media = article.media
        ? getFormattedUrlPath(article.media, imageParmeters)
        : "";

      return {
        ...article,
        summaries: article.summary.split("|").map((text) => text.trim()),
        shareUrl: shortUrl || longURL,
        slug: slug,
        media: media,
        published: dayjs(article.published)
          .tz(dayjsSingaporeTimezone)
          .format("YYYY-MM-DD HH:mm:ss"),
        shareCount: 0,
      };
    });

  return Promise.all(santitizedData);
};

export const getPulseGaTitle = (
  currentSlide: number,
  sortedPulseArticles: SanitizedPulseData[],
  metaTitle: string
) => {
  if (currentSlide === sortedPulseArticles.length) return "pulse outro";

  return metaTitle;
};

/**
 * Generates SEO data for the Pulse page, including title, description, slug, and image URL.
 *
 * @param pulseData - Optional sanitized Pulse data object containing metadata for the page.
 *
 * @returns An object containing the following properties:
 * - `title`: The title of the page, derived from `pulseData.title` or a default static title.
 * - `description`: The description of the page, derived from `pulseData.standfirst`,
 *   `pulseData.summaries[0]`, or a default static description.
 * - `slug`: The URL slug for the page, derived from `pulseData.slug` or a default static slug.
 * - `imageUrl`: The URL of the image, derived from `pulseData.media` or a default static image URL.
 */
export const getPulseSeoData = (
  pulseData?: SanitizedPulseData,
  isHitWithId?: boolean
) => {
  const title = renderPageTitleV2({
    kind: "static",
    title: "Pulse",
  });
  const description = "Your 5 minutes daily news summary.";
  const slug = RouteFactory.pulse;
  const imageUrl = `${URL_ORIGIN}${pulseOgImage}`;

  if (!isHitWithId || !pulseData) {
    return {
      title,
      description,
      slug,
      imageUrl,
    };
  }

  const ogImage = getUpdatedOgImageUrl(pulseData?.media);

  return {
    title: pulseData?.title || title,
    description:
      pulseData?.standfirst || pulseData?.summaries?.[0] || description,
    slug: pulseData?.slug || slug,
    imageUrl: ogImage || imageUrl,
  };
};

export const getRandomArbitrary = (min: number, max: number): number => {
  return Math.round(Math.random() * (max - min) + min);
};

/**
 * Generates gaProps for Pulse Cards
 * @param article - SanitizedPulseData object containing article details
 * @param index - Index of the article in the list
 * @returns GADataProps object for the article
 */

export const getPulseCardGaProps = (
  article: SanitizedPulseData,
  activeArticle: number
): GADataProps => {
  const defaultProps: GADataProps = {
    event: "virtual_pv",
    level2: "pulse",
    contentcat: 1,
    cd_version: "bt2",
  };

  if (article) {
    return {
      ...defaultProps,
      contenttype: "1",
      title: article.title,
      articleid: article.id,
      pubdate: article.published,
      author: article.authors || "(not set)",
      gsChannels: "(not set)",
      pagination: activeArticle,
    };
  }

  return {
    ...defaultProps,
    contenttype: "", // Empty for outro
    title: "pulse outro",
    pagination: activeArticle,
  };
};

/**
 * Returns a record of article IDs and their corresponding share counts
 */
export const objectifyShareCountByArticleId = (
  shareData: PulseShareCount[]
): Record<string, number> => {
  if (!shareData) return {};

  return shareData.reduce<Record<string, number>>((acc, share) => {
    if (share.article_id) {
      acc[share.article_id] = share.share_count;
    }

    return acc;
  }, {});
};
